import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { api } from 'landing-page/helpers';
import { useApi } from 'landing-page/hooks/useApi';

interface SectionHeaderProps {
    title?: string,
    subtitle?: string,
    description?: string,
    autoFetch?: boolean,
    dataType?: string,
    withDescriptionAnimation?: boolean,
    withTitleAnimation?: boolean,
    withLink?: SectionHeaderLink,
    marginBottom?: string
}

interface SectionHeaderLink {
    text: string,
    url: string,
}

interface SectionHeaderItem {
    id: string,
    title_id: string,
    deskripsi_id: string,
    is_active: string | number,
}

const SectionHeaderPlaceholder:React.FC = () => (
    <div className='mb-15 d-flex flex-column gap-3 placeholder-glow'>
        <div className="placeholder bg-dark bg-opacity-10 rounded" style={{ height: '18px', width: '20%' }}></div>
        <div className="placeholder bg-dark bg-opacity-10 rounded" style={{ height: '40px', width: '80%' }}></div>
        <div className="placeholder bg-dark bg-opacity-10 rounded" style={{ height: '40px', width: '50%' }}></div>
    </div>
)

const SectionHeader: React.FC<SectionHeaderProps> = ({
    title,
    subtitle,
    description,
    autoFetch = false,
    dataType = 'section_header_blog',
    withDescriptionAnimation = false,
    withTitleAnimation = true,
    withLink = null,
    marginBottom = 'mb-15'
}) => {
    const [sectionHeader, setSectionHeader] = useState<SectionHeaderProps | null>(null);

    const { data, loading, error, execute: fetchSectionHeader } = useApi(api.getMasterTable.bind(api));

    useEffect(() => {
        if (autoFetch) {
            fetchSectionHeader({ tipe: dataType });
        } else if (autoFetch === false && title !== null) {
            setSectionHeader({ title, subtitle, description });
        }
    }, [autoFetch, dataType, title, subtitle, description]);

    useEffect(() => {
        if (data?.data?.data && Array.isArray(data.data.data)) {
            const filteredItems = data.data.data.filter((item: SectionHeaderItem) => 
                item.is_active === 1 || item.is_active === '1'
            );
            
            if (filteredItems && filteredItems.length > 0) {
                setSectionHeader({
                    title: filteredItems[0].title_id || '',
                    subtitle: filteredItems[0].deskripsi_id || ''
                });
            }
        }
    }, [data]);

    if (loading || !sectionHeader) {
        return <SectionHeaderPlaceholder />;
    }

    return (
        <div className={clsx('d-flex justify-content-between align-items-start align-items-md-center', marginBottom)}>
            <div>
                {withTitleAnimation ? (
                    <h2 
                        className='fw-bold mb-2 fs-5 text-uppercase gradient-text-animation'
                        style={{
                            background: 'linear-gradient(90deg, #FF6B35, #F7931E, #FF8C42, #6B46C1, #4C1D95, #1F2937)',
                            backgroundSize: '200% 100%',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            animation: 'gradientShiftHorizontal 3s ease-in-out infinite',
                            lineHeight: '12px'
                        }}
                    >
                        {sectionHeader?.title}
                    </h2>
                ) : (
                    <h2 className='text-primary fw-bold mb-2 fs-5 uppercase' style={{lineHeight: '12px'}}>{sectionHeader?.title}</h2>
                )}

                {withDescriptionAnimation ? (
                    <p 
                        className='display-4 fw-semibold gradient-text-animation'
                        style={{
                            background: 'linear-gradient(45deg, #FF6B35, #F7931E, #FF8C42, #E55A2B)',
                            backgroundSize: '300% 300%',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            animation: 'gradientShift 3s ease-in-out infinite'
                        }}
                    >
                        {sectionHeader?.subtitle}
                    </p>
                ) : (
                    <p className='display-4 fw-semibold'>{sectionHeader?.subtitle}</p>
                )}

                {sectionHeader?.description && (
                    <p className='fw-semibold fs-5 text-muted'>{sectionHeader?.description}</p>
                )}
            </div>

            {withLink && (
                <div className='d-flex gap-3'>
                    {/* SEMUA button */}
                    <a 
                        href={withLink.url}
                        className='btn btn-light px-4 py-2 fw-semibold text-dark border-0 uppercase btn-sm d-flex align-items-center justify-content-center hover-text-primary' 
                        style={{
                            borderRadius: '24px',
                            transition: 'all 0.3s ease'
                        }}
                        id={`${withLink.text.toLowerCase()}-btn`}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.setProperty('background-color', '#f8f9fa', 'important');
                            e.currentTarget.style.setProperty('color', '#0d6efd', 'important');
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.removeProperty('background-color');
                            e.currentTarget.style.removeProperty('color');
                        }}
                    >
                        {withLink.text}
                    </a>
                    
                    {/* Arrow button */}
                    <div className='position-relative'>
                        <Link  
                            to={withLink.url}
                            className='btn btn-primary rounded-circle d-flex align-items-center justify-content-center position-relative overflow-hidden'
                            style={{
                                width: '48px',
                                height: '48px',
                                zIndex: 1,
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.setProperty('background-color', '#f8f9fa', 'important');
                                const icon = e.currentTarget.querySelector('i');
                                if (icon) {
                                    icon.style.setProperty('color', '#FF6B35', 'important');
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.removeProperty('background-color');
                                const icon = e.currentTarget.querySelector('i');
                                if (icon) {
                                    icon.style.removeProperty('color');
                                }
                            }}
                        >
                            <i 
                                id={`arrow-up-right-${withLink.text.toLowerCase()}-btn`}
                                className='bi bi-arrow-up-right text-white'
                                style={{
                                    fontSize: '18px',
                                    transition: 'color 0.3s ease'
                                }}
                            ></i>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    )
}

export default SectionHeader;